import { useCallback, useEffect, useState } from "react";
import { Check, X, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { api, apiError, isLeader } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { AbsenceDialog, NewButton } from "@/components/RecordDialogs";
import { Button } from "@/components/ui/button";

const statusBadge = {
  pending: "bg-amber-50 text-amber-700 border-amber-200",
  approved: "bg-green-50 text-green-700 border-green-200",
  rejected: "bg-red-50 text-red-700 border-red-200",
};

const StatusIcon = ({ status, id, label }) => (
  <span
    data-testid={`absence-status-icon-${id}`}
    className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-sm font-bold ${
      status === "approved"
        ? "bg-green-100 text-green-700"
        : status === "rejected"
        ? "bg-red-100 text-red-600"
        : "bg-amber-100 text-amber-700"
    }`}
    title={label}
  >
    {status === "approved" ? <Check className="h-4 w-4" /> : status === "rejected" ? <X className="h-4 w-4" /> : "!"}
  </span>
);

export default function Absences() {
  const { t } = useTranslation();
  const { user, company } = useAuth();
  const leader = isLeader(user?.role) && !company?.demo_readonly;
  const [absences, setAbsences] = useState([]);
  const [staff, setStaff] = useState([]);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/absences");
      setAbsences(data);
    } catch (e) {
      toast.error(apiError(e));
    }
  }, []);

  useEffect(() => {
    load();
    api.get("/users").then(({ data }) => setStaff(data)).catch(() => {});
  }, [load]);

  const staffName = (id) => staff.find((s) => s.id === id)?.name || t("common.unknown");

  const setStatus = async (a, status) => {
    try {
      await api.put(`/absences/${a.id}`, { status });
      toast.success(status === "approved" ? t("absPage.approved") : t("absPage.rejected"));
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const remove = async (a) => {
    if (!window.confirm(t("absPage.deleteConfirm"))) return;
    try {
      await api.delete(`/absences/${a.id}`);
      toast.success(t("absPage.deleted"));
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  return (
    <div data-testid="absences-page">
      <div className="mb-4 flex flex-wrap items-end gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 sm:text-3xl">{t("absPage.title")}</h1>
          <p className="text-sm text-slate-500">{absences.length} {t("common.records")}</p>
        </div>
        <div className="ml-auto">
          <NewButton testid="absences-new-button" label={t("absPage.new")} onClick={() => { setActive(null); setOpen(true); }} />
        </div>
      </div>

      <div className="overflow-hidden rounded-lg border border-slate-200 bg-white">
        {absences.length === 0 && (
          <div className="p-8 text-center text-sm text-slate-500" data-testid="absences-empty">
            {t("absPage.empty")}
          </div>
        )}
        {absences.map((a) => (
          <div
            key={a.id}
            data-testid={`absence-row-${a.id}`}
            className="flex flex-wrap items-center gap-3 border-b border-slate-100 p-3 last:border-0 transition-colors duration-200 hover:bg-slate-50"
          >
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-2">
                <StatusIcon status={a.status} id={a.id} label={t(`absStatus.${a.status}`)} />
                <span className="text-sm font-semibold text-slate-900">{staffName(a.user_id)}</span>
                <span className="rounded border border-orange-200 bg-orange-50 px-1.5 py-0.5 text-[11px] font-medium text-orange-800">
                  {t(`absType.${a.type}`)}
                </span>
                <span
                  className={`rounded border px-1.5 py-0.5 text-[11px] font-medium ${statusBadge[a.status]}`}
                  data-testid={`absence-status-${a.id}`}
                >
                  {t(`absStatus.${a.status}`)}
                </span>
              </div>
              <div className="mt-1 text-sm text-slate-600">
                {a.start?.slice(0, 10)} – {a.end?.slice(0, 10)}
                {a.note ? <span className="text-slate-500"> · {a.note}</span> : null}
              </div>
            </div>

            <div className="flex gap-2">
              {leader && a.status !== "approved" && (
                <Button
                  size="sm"
                  data-testid={`absence-approve-${a.id}`}
                  onClick={() => setStatus(a, "approved")}
                  className="h-9 bg-green-600 text-white transition-colors duration-200 hover:bg-green-700"
                >
                  <Check className="mr-1 h-3.5 w-3.5" /> {t("absPage.approve")}
                </Button>
              )}
              {leader && a.status !== "rejected" && (
                <Button
                  variant="outline"
                  size="sm"
                  data-testid={`absence-reject-${a.id}`}
                  onClick={() => setStatus(a, "rejected")}
                  className="h-9 bg-white transition-colors duration-200 hover:bg-slate-100"
                >
                  <X className="h-3.5 w-3.5" />
                </Button>
              )}
              <Button
                variant="outline"
                size="sm"
                data-testid={`absence-edit-${a.id}`}
                onClick={() => { setActive(a); setOpen(true); }}
                className="h-9 bg-white transition-colors duration-200 hover:bg-slate-100"
              >
                <Pencil className="h-3.5 w-3.5" />
              </Button>
              <Button
                variant="outline"
                size="sm"
                data-testid={`absence-delete-row-${a.id}`}
                onClick={() => remove(a)}
                className="h-9 border-red-200 bg-white text-red-600 transition-colors duration-200 hover:bg-red-50"
              >
                <Trash2 className="h-3.5 w-3.5" />
              </Button>
            </div>
          </div>
        ))}
      </div>

      <AbsenceDialog open={open} onOpenChange={setOpen} absence={active} staff={staff} onSaved={load} />
    </div>
  );
}
